export type SponsorTier = 'patrocinado-por' | 'oficial' | 'principal' | 'oro' | 'plata' | 'bronce';

export type SponsorshipEntityType = 'concurso' | 'evento' | 'programa' | 'locutor' | 'radio' | 'club';

export type VisibilityLocation = 'landing' | 'cabina' | 'radio' | 'programa' | 'evento' | 'concurso';

export interface ContestSponsor {
  id: number;
  name: string;
  logo: string;
  website: string;
  whatsapp: string;
  description: string;
  sector: string;
  city: string;
  country: string;
  tier: SponsorTier;
  activeSince: string;
  totalSponsorships: number;
  activeSponsorships: number;
  totalInvestment: number;
}

export interface SponsorshipAssignment {
  id: number;
  sponsorId: number;
  sponsorName: string;
  sponsorLogo: string;
  entityType: SponsorshipEntityType;
  entityName: string;
  entityId: string;
  tier: SponsorTier;
  visibility: VisibilityLocation[];
  startDate: string;
  endDate: string;
  status: 'activo' | 'finalizado' | 'programado';
  investment: number;
  radioName: string;
  radioFrequency: string;
}

export interface SponsorshipMetrics {
  id: number;
  sponsorshipId: number;
  impressions: number;
  clicks: number;
  participations: number;
  conversions: number;
  ctr: number;
  conversionRate: number;
  periodLabel: string;
}

export interface ContestSponsorDashboard {
  activeSponsors: number;
  totalSponsorships: number;
  totalRevenue: number;
  activeCampaigns: number;
  avgCtr: number;
  avgConversion: number;
  totalImpressions: number;
  totalClicks: number;
  totalParticipations: number;
  radioRevenuePercent: number;
}

export interface TierConfig {
  id: SponsorTier;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  order: number;
  description: string;
  priceRange: string;
}

export const tierConfigs: TierConfig[] = [
  {
    id: 'bronce',
    label: 'Sponsor Bronce',
    icon: 'ri-medal-line',
    color: '#CD7F32',
    bgColor: 'bg-amber-50',
    textColor: 'text-amber-800',
    borderColor: 'border-amber-300',
    order: 5,
    description: 'Mención en el concurso o evento. Ideal para negocios locales que empiezan.',
    priceRange: '$50 - $200 USD',
  },
  {
    id: 'plata',
    label: 'Sponsor Plata',
    icon: 'ri-medal-line',
    color: '#A8A8A8',
    bgColor: 'bg-slate-50',
    textColor: 'text-slate-700',
    borderColor: 'border-slate-300',
    order: 4,
    description: 'Mención + logo en pantalla. Visibilidad en la página del evento.',
    priceRange: '$200 - $500 USD',
  },
  {
    id: 'oro',
    label: 'Sponsor Oro',
    icon: 'ri-medal-line',
    color: '#DAA520',
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-800',
    borderColor: 'border-yellow-400',
    order: 3,
    description: 'Logo destacado, mención en vivo, presencia en todas las comunicaciones.',
    priceRange: '$500 - $1,500 USD',
  },
  {
    id: 'principal',
    label: 'Sponsor Principal',
    icon: 'ri-star-fill',
    color: '#E85D04',
    bgColor: 'bg-orange-50',
    textColor: 'text-orange-800',
    borderColor: 'border-orange-400',
    order: 2,
    description: 'Nombre del concurso/evento + marca. Máxima exposición durante toda la duración.',
    priceRange: '$1,500 - $5,000 USD',
  },
  {
    id: 'oficial',
    label: 'Sponsor Oficial',
    icon: 'ri-shield-star-fill',
    color: '#D00000',
    bgColor: 'bg-red-50',
    textColor: 'text-red-700',
    borderColor: 'border-red-400',
    order: 1,
    description: 'Exclusividad de categoría. Sello oficial del evento. Presencia en todos los canales.',
    priceRange: '$5,000 - $15,000 USD',
  },
  {
    id: 'patrocinado-por',
    label: 'Patrocinado por',
    icon: 'ri-vip-crown-fill',
    color: '#6A0DAD',
    bgColor: 'bg-purple-50',
    textColor: 'text-purple-800',
    borderColor: 'border-purple-400',
    order: 0,
    description: 'Naming completo. El concurso/evento lleva el nombre del sponsor. Exclusividad total.',
    priceRange: '$15,000+ USD',
  },
];

export const contestSponsors: ContestSponsor[] = [
  {
    id: 1,
    name: 'Cerveza Patagonia',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20craft%20beer%20brand%20logo%20design%20with%20mountain%20silhouette%2C%20warm%20amber%20and%20forest%20green%20tones%2C%20clean%20modern%20typography%20style%2C%20dark%20background%2C%20premium%20artisanal%20feel%2C%20no%20text%2C%20elegant%20simple%20shapes&width=320&height=160&seq=sponsor-concurso-01&orientation=landscape',
    website: 'https://cervezapatagonia.com',
    whatsapp: '+54 11 5555 0101',
    description: 'Cervecería artesanal líder en Argentina. Patrocinan concursos y eventos musicales en radios de todo el país.',
    sector: 'Bebidas',
    city: 'Bariloche',
    country: 'Argentina',
    tier: 'principal',
    activeSince: '2025-03-01',
    totalSponsorships: 12,
    activeSponsorships: 4,
    totalInvestment: 48000,
  },
  {
    id: 2,
    name: 'Restaurante La Parrilla del Centro',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20steakhouse%20restaurant%20brand%20logo%20with%20flame%20and%20fork%20geometric%20design%2C%20warm%20terracotta%20and%20charcoal%20tones%2C%20modern%20culinary%20style%2C%20dark%20background%2C%20premium%20dining%20feel%2C%20no%20text%2C%20clean%20elegant%20shapes&width=320&height=160&seq=sponsor-concurso-02&orientation=landscape',
    website: 'https://laparrilladelcentro.com',
    whatsapp: '+52 55 9876 5432',
    description: 'Restaurante de cortes argentinos en CDMX. Patrocina concursos gastronómicos y eventos culinarios en radio.',
    sector: 'Gastronomía',
    city: 'Ciudad de México',
    country: 'México',
    tier: 'oro',
    activeSince: '2025-06-15',
    totalSponsorships: 8,
    activeSponsorships: 3,
    totalInvestment: 18500,
  },
  {
    id: 3,
    name: 'Gimnasio PowerFit',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20fitness%20gym%20brand%20logo%20with%20abstract%20dumbbell%20geometric%20shape%2C%20energetic%20orange%20and%20charcoal%20tones%2C%20modern%20athletic%20style%2C%20dark%20background%2C%20powerful%20clean%20design%2C%20no%20text%2C%20dynamic%20simple%20shapes&width=320&height=160&seq=sponsor-concurso-03&orientation=landscape',
    website: 'https://powerfit.mx',
    whatsapp: '+52 33 3456 7890',
    description: 'Cadena de gimnasios en el occidente de México. Patrocina retos fitness, concursos de salud y eventos deportivos en radio.',
    sector: 'Salud y Fitness',
    city: 'Guadalajara',
    country: 'México',
    tier: 'plata',
    activeSince: '2025-09-01',
    totalSponsorships: 6,
    activeSponsorships: 2,
    totalInvestment: 9500,
  },
  {
    id: 4,
    name: 'AutoLux Premium',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20luxury%20car%20dealership%20brand%20logo%20with%20abstract%20shield%20and%20speed%20line%20geometric%20design%2C%20metallic%20silver%20and%20deep%20navy%20tones%2C%20modern%20premium%20automotive%20style%2C%20dark%20background%2C%20elegant%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-04&orientation=landscape',
    website: 'https://autolux.mx',
    whatsapp: '+52 81 2345 6789',
    description: 'Agencia de autos de lujo en el norte de México. Patrocina eventos automotrices, concursos de premios mayores y sorteos.',
    sector: 'Automotriz',
    city: 'Monterrey',
    country: 'México',
    tier: 'oficial',
    activeSince: '2025-04-01',
    totalSponsorships: 10,
    activeSponsorships: 3,
    totalInvestment: 72000,
  },
  {
    id: 5,
    name: 'Hotel Brisas del Pacífico',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20beach%20resort%20hotel%20brand%20logo%20with%20abstract%20palm%20tree%20and%20wave%20geometric%20design%2C%20aqua%20teal%20and%20warm%20sand%20tones%2C%20modern%20tropical%20luxury%20style%2C%20dark%20background%2C%20elegant%20serene%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-05&orientation=landscape',
    website: 'https://hotelbrisas.com',
    whatsapp: '+506 2222 3333',
    description: 'Resort de lujo frente al mar en Costa Rica. Patrocina concursos de viajes, escapadas románticas y eventos turísticos.',
    sector: 'Turismo',
    city: 'San José',
    country: 'Costa Rica',
    tier: 'principal',
    activeSince: '2025-07-01',
    totalSponsorships: 7,
    activeSponsorships: 2,
    totalInvestment: 34000,
  },
  {
    id: 6,
    name: 'TechNova Solutions',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20technology%20solutions%20brand%20logo%20with%20abstract%20circuit%20node%20geometric%20design%2C%20electric%20cyan%20and%20deep%20indigo%20tones%2C%20modern%20futuristic%20style%2C%20dark%20background%2C%20innovative%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-06&orientation=landscape',
    website: 'https://technova.mx',
    whatsapp: '+52 81 4567 8901',
    description: 'Empresa de tecnología con presencia nacional. Patrocina hackathons radiales, concursos de innovación y eventos tech.',
    sector: 'Tecnología',
    city: 'Monterrey',
    country: 'México',
    tier: 'oro',
    activeSince: '2025-02-15',
    totalSponsorships: 9,
    activeSponsorships: 3,
    totalInvestment: 28500,
  },
  {
    id: 7,
    name: 'Librería El Ateneo',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20bookstore%20brand%20logo%20with%20abstract%20open%20book%20and%20columns%20geometric%20design%2C%20warm%20amber%20and%20cream%20tones%2C%20modern%20literary%20classic%20style%2C%20dark%20background%2C%20elegant%20cultural%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-07&orientation=landscape',
    website: 'https://elateneo.com',
    whatsapp: '+54 11 4444 5555',
    description: 'Librería y centro cultural en Buenos Aires. Patrocina concursos literarios, clubes de lectura y eventos culturales en radio.',
    sector: 'Cultura',
    city: 'Buenos Aires',
    country: 'Argentina',
    tier: 'plata',
    activeSince: '2025-11-01',
    totalSponsorships: 5,
    activeSponsorships: 2,
    totalInvestment: 7200,
  },
  {
    id: 8,
    name: 'Universidad Virtual',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20online%20university%20brand%20logo%20with%20abstract%20graduation%20cap%20and%20digital%20globe%20geometric%20design%2C%20deep%20blue%20and%20gold%20tones%2C%20modern%20academic%20style%2C%20dark%20background%2C%20prestigious%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-08&orientation=landscape',
    website: 'https://uvirtual.edu.mx',
    whatsapp: '+52 55 5555 6666',
    description: 'Universidad en línea con presencia en 4 países. Patrocina becas, concursos académicos y eventos educativos en radio.',
    sector: 'Educación',
    city: 'Buenos Aires',
    country: 'Argentina',
    tier: 'oficial',
    activeSince: '2025-01-01',
    totalSponsorships: 14,
    activeSponsorships: 5,
    totalInvestment: 92000,
  },
  {
    id: 9,
    name: 'Festival de la Cumbia',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20music%20festival%20brand%20logo%20with%20abstract%20sound%20wave%20and%20dance%20silhouette%20geometric%20design%2C%20vibrant%20magenta%20and%20gold%20tones%2C%20modern%20festive%20latin%20style%2C%20dark%20background%2C%20energetic%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-09&orientation=landscape',
    website: 'https://festivalcumbia.com',
    whatsapp: '+57 4 333 4455',
    description: 'El festival de música tropical más grande de Colombia. Patrocina concursos de baile, eventos en vivo y batallas de ritmos.',
    sector: 'Entretenimiento',
    city: 'Medellín',
    country: 'Colombia',
    tier: 'patrocinado-por',
    activeSince: '2025-05-01',
    totalSponsorships: 6,
    activeSponsorships: 2,
    totalInvestment: 65000,
  },
  {
    id: 10,
    name: 'Clínica Dental Sonrisa',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20dental%20clinic%20brand%20logo%20with%20abstract%20tooth%20and%20smile%20geometric%20design%2C%20clean%20white%20and%20sky%20blue%20tones%2C%20modern%20healthcare%20style%2C%20dark%20background%2C%20friendly%20professional%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-10&orientation=landscape',
    website: 'https://clinicasonrisa.mx',
    whatsapp: '+52 33 777 8899',
    description: 'Clínica dental con 3 sucursales en Guadalajara. Patrocina concursos de salud bucal y eventos de bienestar comunitario.',
    sector: 'Salud',
    city: 'Guadalajara',
    country: 'México',
    tier: 'bronce',
    activeSince: '2026-01-15',
    totalSponsorships: 3,
    activeSponsorships: 1,
    totalInvestment: 2400,
  },
  {
    id: 11,
    name: 'Banco Santander',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20banking%20financial%20brand%20logo%20with%20abstract%20flame%20and%20shield%20geometric%20design%2C%20bold%20red%20and%20white%20tones%2C%20modern%20corporate%20style%2C%20dark%20background%2C%20trustworthy%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-11&orientation=landscape',
    website: 'https://santander.com.mx',
    whatsapp: '+52 55 1234 5678',
    description: 'Banco líder en México y Latinoamérica. Patrocina grandes concursos con premios en efectivo y eventos financieros educativos.',
    sector: 'Financiero',
    city: 'Ciudad de México',
    country: 'México',
    tier: 'patrocinado-por',
    activeSince: '2024-08-01',
    totalSponsorships: 18,
    activeSponsorships: 6,
    totalInvestment: 145000,
  },
  {
    id: 12,
    name: 'Ferretería Don Pepe',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20hardware%20store%20brand%20logo%20with%20abstract%20wrench%20and%20house%20geometric%20design%2C%20warm%20rust%20orange%20and%20steel%20gray%20tones%2C%20modern%20industrial%20style%2C%20dark%20background%2C%20friendly%20neighborhood%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-12&orientation=landscape',
    website: 'https://ferreteriadonpepe.mx',
    whatsapp: '+52 686 222 3344',
    description: 'Ferretería de barrio con 40 años de historia en Mexicali. Patrocina concursos locales, trivias barriales y eventos comunitarios.',
    sector: 'Comercio',
    city: 'Mexicali',
    country: 'México',
    tier: 'bronce',
    activeSince: '2025-12-01',
    totalSponsorships: 2,
    activeSponsorships: 1,
    totalInvestment: 1200,
  },
  {
    id: 13,
    name: 'Aerolínea Viva Air',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20airline%20brand%20logo%20with%20abstract%20flying%20bird%20and%20wing%20geometric%20design%2C%20sky%20blue%20and%20white%20tones%2C%20modern%20aviation%20style%2C%20dark%20background%2C%20dynamic%20clean%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-13&orientation=landscape',
    website: 'https://vivaair.com',
    whatsapp: '+57 1 555 0101',
    description: 'Aerolínea de bajo costo con rutas en toda Latinoamérica. Patrocina concursos de viajes, pasajes dobles y eventos de turismo.',
    sector: 'Aviación',
    city: 'Bogotá',
    country: 'Colombia',
    tier: 'principal',
    activeSince: '2025-06-01',
    totalSponsorships: 8,
    activeSponsorships: 3,
    totalInvestment: 52000,
  },
  {
    id: 14,
    name: 'Panadería El Buen Sabor',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20bakery%20brand%20logo%20with%20abstract%20wheat%20and%20bread%20loaf%20geometric%20design%2C%20warm%20golden%20and%20brown%20tones%2C%20modern%20artisanal%20style%2C%20dark%20background%2C%20cozy%20inviting%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-14&orientation=landscape',
    website: 'https://elbuensabor.mx',
    whatsapp: '+52 999 111 2233',
    description: 'Panadería tradicional yucateca. Patrocina concursos de cocina, trivias matutinas y eventos gastronómicos locales.',
    sector: 'Alimentación',
    city: 'Mérida',
    country: 'México',
    tier: 'bronce',
    activeSince: '2026-02-01',
    totalSponsorships: 4,
    activeSponsorships: 2,
    totalInvestment: 1800,
  },
  {
    id: 15,
    name: 'Movistar',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20telecommunications%20brand%20logo%20with%20abstract%20signal%20waves%20and%20connection%20geometric%20design%2C%20vibrant%20green%20and%20cyan%20tones%2C%20modern%20tech%20style%2C%20dark%20background%2C%20dynamic%20forward%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-15&orientation=landscape',
    website: 'https://movistar.com',
    whatsapp: '+52 55 8000 1234',
    description: 'Operador de telecomunicaciones líder en Latinoamérica. Patrocina mega eventos, conciertos transmitidos y concursos digitales.',
    sector: 'Telecomunicaciones',
    city: 'Ciudad de México',
    country: 'México',
    tier: 'patrocinado-por',
    activeSince: '2024-06-01',
    totalSponsorships: 22,
    activeSponsorships: 7,
    totalInvestment: 210000,
  },
  {
    id: 16,
    name: 'Óptica Visión Clara',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20optometry%20brand%20logo%20with%20abstract%20eye%20and%20lens%20geometric%20design%2C%20clean%20blue%20and%20silver%20tones%2C%20modern%20healthcare%20style%2C%20dark%20background%2C%20professional%20precise%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-16&orientation=landscape',
    website: 'https://opticavisionclara.com',
    whatsapp: '+52 55 3333 4444',
    description: 'Cadena de ópticas con 15 sucursales en CDMX. Patrocina concursos de salud visual y eventos de concientización.',
    sector: 'Salud',
    city: 'Ciudad de México',
    country: 'México',
    tier: 'plata',
    activeSince: '2026-01-01',
    totalSponsorships: 3,
    activeSponsorships: 1,
    totalInvestment: 4500,
  },
  {
    id: 17,
    name: 'RadioShack Electrónica',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20electronics%20retail%20brand%20logo%20with%20abstract%20circuit%20and%20plug%20geometric%20design%2C%20electric%20blue%20and%20red%20tones%2C%20modern%20retail%20style%2C%20dark%20background%2C%20tech%20gadget%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-17&orientation=landscape',
    website: 'https://radioshack.com.mx',
    whatsapp: '+52 55 6666 7777',
    description: 'Tienda de electrónicos con presencia nacional. Patrocina concursos de tecnología, trivia geek y eventos gaming en radio.',
    sector: 'Tecnología',
    city: 'Ciudad de México',
    country: 'México',
    tier: 'oro',
    activeSince: '2025-08-01',
    totalSponsorships: 6,
    activeSponsorships: 2,
    totalInvestment: 15000,
  },
  {
    id: 18,
    name: 'Cinepolis',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20cinema%20chain%20brand%20logo%20with%20abstract%20film%20reel%20and%20star%20geometric%20design%2C%20rich%20burgundy%20and%20gold%20tones%2C%20modern%20entertainment%20style%2C%20dark%20background%2C%20premium%20cinematic%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-18&orientation=landscape',
    website: 'https://cinepolis.com',
    whatsapp: '+52 55 8000 5678',
    description: 'La cadena de cines más grande de Latinoamérica. Patrocina premieres, concursos de cine, trivia de películas y eventos de alfombra roja.',
    sector: 'Entretenimiento',
    city: 'Morelia',
    country: 'México',
    tier: 'oficial',
    activeSince: '2024-10-01',
    totalSponsorships: 15,
    activeSponsorships: 4,
    totalInvestment: 88000,
  },
  {
    id: 19,
    name: 'Jugos Naturales VitaFruta',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20fresh%20juice%20brand%20logo%20with%20abstract%20fruit%20and%20leaf%20geometric%20design%2C%20vibrant%20orange%20and%20green%20tones%2C%20modern%20organic%20style%2C%20dark%20background%2C%20healthy%20refreshing%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-19&orientation=landscape',
    website: 'https://vitafruta.com',
    whatsapp: '+52 55 2222 3333',
    description: 'Cadena de juguerías saludables en CDMX y área metropolitana. Patrocina concursos de bienestar, retos fitness y eventos saludables.',
    sector: 'Alimentación',
    city: 'Ciudad de México',
    country: 'México',
    tier: 'plata',
    activeSince: '2026-03-01',
    totalSponsorships: 2,
    activeSponsorships: 1,
    totalInvestment: 3000,
  },
  {
    id: 20,
    name: 'Seguros La Confianza',
    logo: 'https://readdy.ai/api/search-image?query=Minimalist%20insurance%20company%20brand%20logo%20with%20abstract%20umbrella%20shield%20geometric%20design%2C%20deep%20teal%20and%20copper%20tones%2C%20modern%20financial%20protection%20style%2C%20dark%20background%2C%20trustworthy%20solid%20shapes%2C%20no%20text&width=320&height=160&seq=sponsor-concurso-20&orientation=landscape',
    website: 'https://seguroslaconfianza.com',
    whatsapp: '+52 55 4444 5555',
    description: 'Aseguradora con 25 años en el mercado mexicano. Patrocina concursos de protección familiar y eventos de educación financiera.',
    sector: 'Seguros',
    city: 'Guadalajara',
    country: 'México',
    tier: 'oro',
    activeSince: '2025-05-01',
    totalSponsorships: 7,
    activeSponsorships: 3,
    totalInvestment: 22000,
  },
];

export const sponsorshipAssignments: SponsorshipAssignment[] = [
  {
    id: 2001,
    sponsorId: 1,
    sponsorName: 'Cerveza Patagonia',
    sponsorLogo: contestSponsors[0].logo,
    entityType: 'evento',
    entityName: 'Festival de la Cerveza Artesanal 2026',
    entityId: 'evt-festival-cerveza-2026',
    tier: 'patrocinado-por',
    visibility: ['landing', 'radio', 'evento'],
    startDate: '2026-06-01',
    endDate: '2026-08-15',
    status: 'activo',
    investment: 15000,
    radioName: 'Radio Éxito FM',
    radioFrequency: '98.5 FM',
  },
  {
    id: 2002,
    sponsorId: 11,
    sponsorName: 'Banco Santander',
    sponsorLogo: contestSponsors[10].logo,
    entityType: 'concurso',
    entityName: 'El Gran Sorteo del Verano',
    entityId: 'cnc-sorteo-verano-2026',
    tier: 'patrocinado-por',
    visibility: ['landing', 'cabina', 'radio', 'concurso'],
    startDate: '2026-07-01',
    endDate: '2026-08-31',
    status: 'programado',
    investment: 25000,
    radioName: 'Radio Industria',
    radioFrequency: '93.5 FM',
  },
  {
    id: 2003,
    sponsorId: 15,
    sponsorName: 'Movistar',
    sponsorLogo: contestSponsors[14].logo,
    entityType: 'evento',
    entityName: 'Concierto Movistar Éxito 2026',
    entityId: 'evt-concierto-movistar-2026',
    tier: 'patrocinado-por',
    visibility: ['landing', 'cabina', 'radio', 'programa', 'evento'],
    startDate: '2026-05-01',
    endDate: '2026-09-30',
    status: 'activo',
    investment: 35000,
    radioName: 'Estéreo Capital',
    radioFrequency: '102.1 FM',
  },
  {
    id: 2004,
    sponsorId: 18,
    sponsorName: 'Cinepolis',
    sponsorLogo: contestSponsors[17].logo,
    entityType: 'concurso',
    entityName: 'Trivia de Películas: Edición Verano',
    entityId: 'cnc-trivia-cine-verano-2026',
    tier: 'oficial',
    visibility: ['landing', 'radio', 'programa', 'concurso'],
    startDate: '2026-06-15',
    endDate: '2026-08-15',
    status: 'activo',
    investment: 12000,
    radioName: 'Radio Costa Azul',
    radioFrequency: '88.7 FM',
  },
  {
    id: 2005,
    sponsorId: 8,
    sponsorName: 'Universidad Virtual',
    sponsorLogo: contestSponsors[7].logo,
    entityType: 'concurso',
    entityName: 'Becas Talento RFW 2026',
    entityId: 'cnc-becas-talento-2026',
    tier: 'oficial',
    visibility: ['landing', 'cabina', 'radio', 'concurso'],
    startDate: '2026-04-01',
    endDate: '2026-09-30',
    status: 'activo',
    investment: 18000,
    radioName: 'Radio Cultura Digital',
    radioFrequency: '91.3 FM',
  },
  {
    id: 2006,
    sponsorId: 4,
    sponsorName: 'AutoLux Premium',
    sponsorLogo: contestSponsors[3].logo,
    entityType: 'concurso',
    entityName: 'Gana un Auto 0km',
    entityId: 'cnc-auto-0km-2026',
    tier: 'oficial',
    visibility: ['landing', 'cabina', 'radio', 'programa', 'concurso'],
    startDate: '2026-03-01',
    endDate: '2026-09-30',
    status: 'activo',
    investment: 28000,
    radioName: 'Radio Deportiva Total',
    radioFrequency: '95.7 FM',
  },
  {
    id: 2007,
    sponsorId: 5,
    sponsorName: 'Hotel Brisas del Pacífico',
    sponsorLogo: contestSponsors[4].logo,
    entityType: 'concurso',
    entityName: 'Escapada de Fin de Semana',
    entityId: 'cnc-escapada-2026',
    tier: 'principal',
    visibility: ['landing', 'radio', 'concurso'],
    startDate: '2026-05-15',
    endDate: '2026-08-30',
    status: 'activo',
    investment: 7500,
    radioName: 'Radio Peninsular',
    radioFrequency: '101.9 FM',
  },
  {
    id: 2008,
    sponsorId: 13,
    sponsorName: 'Aerolínea Viva Air',
    sponsorLogo: contestSponsors[12].logo,
    entityType: 'concurso',
    entityName: 'Viaje Doble a Cartagena',
    entityId: 'cnc-viaje-cartagena-2026',
    tier: 'principal',
    visibility: ['landing', 'radio', 'concurso'],
    startDate: '2026-06-01',
    endDate: '2026-09-15',
    status: 'activo',
    investment: 12000,
    radioName: 'Radio Estéreo Capital',
    radioFrequency: '102.1 FM',
  },
  {
    id: 2009,
    sponsorId: 2,
    sponsorName: 'Restaurante La Parrilla del Centro',
    sponsorLogo: contestSponsors[1].logo,
    entityType: 'evento',
    entityName: 'Noche de Parrilla y Radio',
    entityId: 'evt-parrilla-radio-2026',
    tier: 'oro',
    visibility: ['radio', 'evento'],
    startDate: '2026-07-01',
    endDate: '2026-07-15',
    status: 'programado',
    investment: 3500,
    radioName: 'Radio Éxito FM',
    radioFrequency: '98.5 FM',
  },
  {
    id: 2010,
    sponsorId: 6,
    sponsorName: 'TechNova Solutions',
    sponsorLogo: contestSponsors[5].logo,
    entityType: 'programa',
    entityName: 'Mundo Digital',
    entityId: 'prg-mundo-digital',
    tier: 'oro',
    visibility: ['landing', 'radio', 'programa'],
    startDate: '2026-04-01',
    endDate: '2026-10-01',
    status: 'activo',
    investment: 8000,
    radioName: 'Radio Élite Digital',
    radioFrequency: '99.1 FM',
  },
  {
    id: 2011,
    sponsorId: 20,
    sponsorName: 'Seguros La Confianza',
    sponsorLogo: contestSponsors[19].logo,
    entityType: 'locutor',
    entityName: 'Valentina Ríos',
    entityId: 'hst-valentina-rios',
    tier: 'oro',
    visibility: ['landing', 'radio', 'locutor'],
    startDate: '2026-05-01',
    endDate: '2026-11-01',
    status: 'activo',
    investment: 6000,
    radioName: 'La Mexicana FM',
    radioFrequency: '104.7 FM',
  },
  {
    id: 2012,
    sponsorId: 7,
    sponsorName: 'Librería El Ateneo',
    sponsorLogo: contestSponsors[6].logo,
    entityType: 'club',
    entityName: 'Club Oficial de Radio Cultura Digital',
    entityId: 'clb-radio-cultura-digital',
    tier: 'plata',
    visibility: ['radio', 'club'],
    startDate: '2026-03-01',
    endDate: '2026-09-01',
    status: 'activo',
    investment: 2500,
    radioName: 'Radio Cultura Digital',
    radioFrequency: '91.3 FM',
  },
  {
    id: 2013,
    sponsorId: 17,
    sponsorName: 'RadioShack Electrónica',
    sponsorLogo: contestSponsors[16].logo,
    entityType: 'concurso',
    entityName: 'Trivia Geek del Mes',
    entityId: 'cnc-trivia-geek-2026',
    tier: 'oro',
    visibility: ['landing', 'radio', 'concurso'],
    startDate: '2026-06-01',
    endDate: '2026-12-01',
    status: 'activo',
    investment: 4500,
    radioName: 'Radio Élite Digital',
    radioFrequency: '99.1 FM',
  },
  {
    id: 2014,
    sponsorId: 1,
    sponsorName: 'Cerveza Patagonia',
    sponsorLogo: contestSponsors[0].logo,
    entityType: 'concurso',
    entityName: 'Trivia Cervecera: ¿Cuánto sabés?',
    entityId: 'cnc-trivia-cerveza-2026',
    tier: 'principal',
    visibility: ['landing', 'cabina', 'radio', 'programa', 'concurso'],
    startDate: '2026-06-01',
    endDate: '2026-08-31',
    status: 'activo',
    investment: 6000,
    radioName: 'Radio Industria',
    radioFrequency: '93.5 FM',
  },
  {
    id: 2015,
    sponsorId: 16,
    sponsorName: 'Óptica Visión Clara',
    sponsorLogo: contestSponsors[15].logo,
    entityType: 'evento',
    entityName: 'Semana de la Salud Visual',
    entityId: 'evt-salud-visual-2026',
    tier: 'plata',
    visibility: ['radio', 'evento'],
    startDate: '2026-08-01',
    endDate: '2026-08-07',
    status: 'programado',
    investment: 2500,
    radioName: 'Radio Éxito FM',
    radioFrequency: '98.5 FM',
  },
  {
    id: 2016,
    sponsorId: 14,
    sponsorName: 'Panadería El Buen Sabor',
    sponsorLogo: contestSponsors[13].logo,
    entityType: 'concurso',
    entityName: 'Recetas de la Abuela',
    entityId: 'cnc-recetas-abuela-2026',
    tier: 'bronce',
    visibility: ['radio', 'concurso'],
    startDate: '2026-06-01',
    endDate: '2026-07-31',
    status: 'activo',
    investment: 800,
    radioName: 'Radio Peninsular',
    radioFrequency: '101.9 FM',
  },
  {
    id: 2017,
    sponsorId: 3,
    sponsorName: 'Gimnasio PowerFit',
    sponsorLogo: contestSponsors[2].logo,
    entityType: 'concurso',
    entityName: 'Reto 30 Días Fit',
    entityId: 'cnc-reto-30-dias-2026',
    tier: 'plata',
    visibility: ['landing', 'radio', 'concurso'],
    startDate: '2026-07-01',
    endDate: '2026-08-15',
    status: 'programado',
    investment: 3000,
    radioName: 'Radio Deportiva Total',
    radioFrequency: '95.7 FM',
  },
  {
    id: 2018,
    sponsorId: 19,
    sponsorName: 'Jugos Naturales VitaFruta',
    sponsorLogo: contestSponsors[18].logo,
    entityType: 'evento',
    entityName: 'Mañana Saludable en Vivo',
    entityId: 'evt-manana-saludable-2026',
    tier: 'plata',
    visibility: ['radio', 'programa', 'evento'],
    startDate: '2026-06-20',
    endDate: '2026-09-20',
    status: 'activo',
    investment: 1800,
    radioName: 'La Mexicana FM',
    radioFrequency: '104.7 FM',
  },
  {
    id: 2019,
    sponsorId: 10,
    sponsorName: 'Clínica Dental Sonrisa',
    sponsorLogo: contestSponsors[9].logo,
    entityType: 'concurso',
    entityName: 'Sonrisa del Mes',
    entityId: 'cnc-sonrisa-mes-2026',
    tier: 'bronce',
    visibility: ['radio', 'concurso'],
    startDate: '2026-06-01',
    endDate: '2026-12-01',
    status: 'activo',
    investment: 1200,
    radioName: 'Radio Costa Azul',
    radioFrequency: '88.7 FM',
  },
  {
    id: 2020,
    sponsorId: 12,
    sponsorName: 'Ferretería Don Pepe',
    sponsorLogo: contestSponsors[11].logo,
    entityType: 'concurso',
    entityName: 'Trivia Mañanera del Barrio',
    entityId: 'cnc-trivia-barrio-2026',
    tier: 'bronce',
    visibility: ['radio', 'concurso'],
    startDate: '2026-06-01',
    endDate: '2026-08-31',
    status: 'activo',
    investment: 600,
    radioName: 'Radio Peninsular',
    radioFrequency: '101.9 FM',
  },
  {
    id: 2021,
    sponsorId: 9,
    sponsorName: 'Festival de la Cumbia',
    sponsorLogo: contestSponsors[8].logo,
    entityType: 'evento',
    entityName: 'Batalla de Cumbia 2026',
    entityId: 'evt-batalla-cumbia-2026',
    tier: 'patrocinado-por',
    visibility: ['landing', 'cabina', 'radio', 'evento'],
    startDate: '2026-07-15',
    endDate: '2026-08-31',
    status: 'programado',
    investment: 20000,
    radioName: 'Radio Estéreo Capital',
    radioFrequency: '102.1 FM',
  },
  {
    id: 2022,
    sponsorId: 15,
    sponsorName: 'Movistar',
    sponsorLogo: contestSponsors[14].logo,
    entityType: 'programa',
    entityName: 'Capital Mix',
    entityId: 'prg-capital-mix',
    tier: 'principal',
    visibility: ['landing', 'radio', 'programa'],
    startDate: '2026-04-01',
    endDate: '2026-10-01',
    status: 'activo',
    investment: 10000,
    radioName: 'Estéreo Capital',
    radioFrequency: '102.1 FM',
  },
  {
    id: 2023,
    sponsorId: 9,
    sponsorName: 'Festival de la Cumbia',
    sponsorLogo: contestSponsors[8].logo,
    entityType: 'club',
    entityName: 'Club Oficial de Estéreo Capital',
    entityId: 'clb-estereo-capital',
    tier: 'principal',
    visibility: ['landing', 'radio', 'club'],
    startDate: '2026-05-01',
    endDate: '2026-11-01',
    status: 'activo',
    investment: 8000,
    radioName: 'Estéreo Capital',
    radioFrequency: '102.1 FM',
  },
  {
    id: 2024,
    sponsorId: 3,
    sponsorName: 'Gimnasio PowerFit',
    sponsorLogo: contestSponsors[2].logo,
    entityType: 'locutor',
    entityName: 'Roberto Aguilar',
    entityId: 'hst-roberto-aguilar',
    tier: 'plata',
    visibility: ['radio', 'locutor'],
    startDate: '2026-06-01',
    endDate: '2026-12-01',
    status: 'activo',
    investment: 3000,
    radioName: 'Radio Deportiva Total',
    radioFrequency: '95.7 FM',
  },
  {
    id: 2025,
    sponsorId: 11,
    sponsorName: 'Banco Santander',
    sponsorLogo: contestSponsors[10].logo,
    entityType: 'radio',
    entityName: 'Radio Éxito FM',
    entityId: 'rad-exito-fm',
    tier: 'oficial',
    visibility: ['landing', 'cabina', 'radio'],
    startDate: '2026-01-01',
    endDate: '2026-12-31',
    status: 'activo',
    investment: 35000,
    radioName: 'Radio Éxito FM',
    radioFrequency: '98.5 FM',
  },
];

export const sponsorshipMetricsData: SponsorshipMetrics[] = [
  { id: 3001, sponsorshipId: 2001, impressions: 128000, clicks: 18500, participations: 4200, conversions: 850, ctr: 14.5, conversionRate: 4.6, periodLabel: 'Jun-Ago 2026' },
  { id: 3002, sponsorshipId: 2003, impressions: 245000, clicks: 32000, participations: 8900, conversions: 2100, ctr: 13.1, conversionRate: 6.6, periodLabel: 'May-Sep 2026' },
  { id: 3003, sponsorshipId: 2004, impressions: 86000, clicks: 15800, participations: 7200, conversions: 1800, ctr: 18.4, conversionRate: 11.4, periodLabel: 'Jun-Ago 2026' },
  { id: 3004, sponsorshipId: 2005, impressions: 192000, clicks: 28500, participations: 10500, conversions: 3200, ctr: 14.8, conversionRate: 11.2, periodLabel: 'Abr-Sep 2026' },
  { id: 3005, sponsorshipId: 2006, impressions: 310000, clicks: 42000, participations: 15000, conversions: 2800, ctr: 13.5, conversionRate: 6.7, periodLabel: 'Mar-Sep 2026' },
  { id: 3006, sponsorshipId: 2007, impressions: 54000, clicks: 9200, participations: 3800, conversions: 620, ctr: 17.0, conversionRate: 6.7, periodLabel: 'May-Ago 2026' },
  { id: 3007, sponsorshipId: 2008, impressions: 78000, clicks: 14100, participations: 5600, conversions: 940, ctr: 18.1, conversionRate: 6.7, periodLabel: 'Jun-Sep 2026' },
  { id: 3008, sponsorshipId: 2010, impressions: 156000, clicks: 22800, participations: 0, conversions: 0, ctr: 14.6, conversionRate: 0, periodLabel: 'Abr-Oct 2026' },
  { id: 3009, sponsorshipId: 2011, impressions: 92000, clicks: 13500, participations: 0, conversions: 0, ctr: 14.7, conversionRate: 0, periodLabel: 'May-Nov 2026' },
  { id: 3010, sponsorshipId: 2014, impressions: 65000, clicks: 11200, participations: 4800, conversions: 1100, ctr: 17.2, conversionRate: 9.8, periodLabel: 'Jun-Ago 2026' },
  { id: 3011, sponsorshipId: 2013, impressions: 42000, clicks: 8200, participations: 3500, conversions: 520, ctr: 19.5, conversionRate: 6.3, periodLabel: 'Jun-Dic 2026' },
  { id: 3012, sponsorshipId: 2025, impressions: 420000, clicks: 58000, participations: 0, conversions: 0, ctr: 13.8, conversionRate: 0, periodLabel: 'Ene-Dic 2026' },
  { id: 3013, sponsorshipId: 2016, impressions: 12000, clicks: 2100, participations: 850, conversions: 120, ctr: 17.5, conversionRate: 5.7, periodLabel: 'Jun-Jul 2026' },
  { id: 3014, sponsorshipId: 2018, impressions: 28000, clicks: 4200, participations: 0, conversions: 0, ctr: 15.0, conversionRate: 0, periodLabel: 'Jun-Sep 2026' },
  { id: 3015, sponsorshipId: 2022, impressions: 185000, clicks: 26500, participations: 0, conversions: 0, ctr: 14.3, conversionRate: 0, periodLabel: 'Abr-Oct 2026' },
  { id: 3016, sponsorshipId: 2012, impressions: 35000, clicks: 5200, participations: 0, conversions: 0, ctr: 14.9, conversionRate: 0, periodLabel: 'Mar-Sep 2026' },
  { id: 3017, sponsorshipId: 2019, impressions: 8000, clicks: 1100, participations: 420, conversions: 65, ctr: 13.8, conversionRate: 5.9, periodLabel: 'Jun-Dic 2026' },
  { id: 3018, sponsorshipId: 2020, impressions: 5000, clicks: 900, participations: 380, conversions: 55, ctr: 18.0, conversionRate: 6.1, periodLabel: 'Jun-Ago 2026' },
  { id: 3019, sponsorshipId: 2024, impressions: 45000, clicks: 6800, participations: 0, conversions: 0, ctr: 15.1, conversionRate: 0, periodLabel: 'Jun-Dic 2026' },
  { id: 3020, sponsorshipId: 2023, impressions: 72000, clicks: 10800, participations: 0, conversions: 0, ctr: 15.0, conversionRate: 0, periodLabel: 'May-Nov 2026' },
  { id: 3021, sponsorshipId: 2025, impressions: 420000, clicks: 58000, participations: 0, conversions: 0, ctr: 13.8, conversionRate: 0, periodLabel: 'Ene-Dic 2026' },
];

export const contestSponsorDashboard: ContestSponsorDashboard = {
  activeSponsors: 20,
  totalSponsorships: 25,
  totalRevenue: 303400,
  activeCampaigns: 16,
  avgCtr: 15.6,
  avgConversion: 7.1,
  totalImpressions: 2675000,
  totalClicks: 408300,
  totalParticipations: 65150,
  radioRevenuePercent: 100,
};

export const entityTypeConfig = [
  { id: 'concurso' as SponsorshipEntityType, label: 'Concurso', icon: 'ri-trophy-line', color: 'bg-amber-100', textColor: 'text-amber-700', dotColor: 'bg-amber-500' },
  { id: 'evento' as SponsorshipEntityType, label: 'Evento', icon: 'ri-calendar-event-line', color: 'bg-rose-100', textColor: 'text-rose-700', dotColor: 'bg-rose-500' },
  { id: 'programa' as SponsorshipEntityType, label: 'Programa', icon: 'ri-music-line', color: 'bg-emerald-100', textColor: 'text-emerald-700', dotColor: 'bg-emerald-500' },
  { id: 'locutor' as SponsorshipEntityType, label: 'Locutor', icon: 'ri-mic-line', color: 'bg-sky-100', textColor: 'text-sky-700', dotColor: 'bg-sky-500' },
  { id: 'radio' as SponsorshipEntityType, label: 'Radio', icon: 'ri-radio-line', color: 'bg-violet-100', textColor: 'text-violet-700', dotColor: 'bg-violet-500' },
  { id: 'club' as SponsorshipEntityType, label: 'Club', icon: 'ri-group-line', color: 'bg-orange-100', textColor: 'text-orange-700', dotColor: 'bg-orange-500' },
];

export const visibilityConfig = [
  { id: 'landing' as VisibilityLocation, label: 'Landing', icon: 'ri-home-line', color: 'bg-pink-100', textColor: 'text-pink-700' },
  { id: 'cabina' as VisibilityLocation, label: 'Cabina', icon: 'ri-live-line', color: 'bg-red-100', textColor: 'text-red-700' },
  { id: 'radio' as VisibilityLocation, label: 'Radio', icon: 'ri-radio-line', color: 'bg-violet-100', textColor: 'text-violet-700' },
  { id: 'programa' as VisibilityLocation, label: 'Programa', icon: 'ri-music-line', color: 'bg-emerald-100', textColor: 'text-emerald-700' },
  { id: 'evento' as VisibilityLocation, label: 'Evento', icon: 'ri-calendar-event-line', color: 'bg-rose-100', textColor: 'text-rose-700' },
  { id: 'concurso' as VisibilityLocation, label: 'Concurso', icon: 'ri-trophy-line', color: 'bg-amber-100', textColor: 'text-amber-700' },
];

export function getSponsorById(id: number): ContestSponsor | undefined {
  return contestSponsors.find((s) => s.id === id);
}

export function getSponsorsByTier(tier: SponsorTier): ContestSponsor[] {
  return contestSponsors.filter((s) => s.tier === tier);
}

export function getAssignmentsByEntityType(type: SponsorshipEntityType): SponsorshipAssignment[] {
  return sponsorshipAssignments.filter((a) => a.entityType === type);
}

export function getAssignmentsBySponsor(sponsorId: number): SponsorshipAssignment[] {
  return sponsorshipAssignments.filter((a) => a.sponsorId === sponsorId);
}

export function getAssignmentsByStatus(status: 'activo' | 'finalizado' | 'programado'): SponsorshipAssignment[] {
  return sponsorshipAssignments.filter((a) => a.status === status);
}

export function getMetricsBySponsorshipId(sponsorshipId: number): SponsorshipMetrics | undefined {
  return sponsorshipMetricsData.find((m) => m.sponsorshipId === sponsorshipId);
}

export function getActiveAssignments(): SponsorshipAssignment[] {
  return sponsorshipAssignments.filter((a) => a.status === 'activo');
}

export function formatMoney(amount: number): string {
  if (amount >= 1000) return `$${(amount / 1000).toFixed(1)}K`;
  return `$${amount}`;
}

export function formatCompactNum(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
}

export function getEntityTypeConfig(type: SponsorshipEntityType) {
  return entityTypeConfig.find((c) => c.id === type);
}

export function getTierConfig(tier: SponsorTier): TierConfig | undefined {
  return tierConfigs.find((t) => t.id === tier);
}

export function getVisibilityConfig(loc: VisibilityLocation) {
  return visibilityConfig.find((v) => v.id === loc);
}